// Strip trailing slash so we never produce double-slash URLs
const BaseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
const LOGS_ENDPOINT = `${BaseUrl}/api/logs`;
const FLUSH_INTERVAL_MS = 2000;

let queue = [];
let flushTimer = null;

const sendBatch = (logs, useBeacon = false) => {
  if (!logs.length) return;
  const payload = JSON.stringify({ logs });

  // On tab-hide/unload a normal fetch can be cancelled mid-flight; sendBeacon
  // is designed to survive that, so we prefer it there even though it can't
  // carry an Authorization header (the /api/logs route accepts unauthenticated
  // writes for exactly this reason — see loggerRoutes.js).
  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    const delivered = navigator.sendBeacon(LOGS_ENDPOINT, blob);
    if (delivered) return;
    // fall through to fetch if the beacon couldn't be queued
  }

  const token = sessionStorage.getItem("accessToken");
  fetch(LOGS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: payload,
    keepalive: useBeacon, // let the request outlive page unload when relevant
  }).catch((error) => {
    // Intentionally silent to the user — logging must never crash the app
    // eslint-disable-next-line no-console
    console.error("Failed to send logs to backend:", error);
  });
};

const flush = (useBeacon = false) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  sendBatch(batch, useBeacon);
};

const enqueue = (level, message, meta, immediate) => {
  queue.push({ level, message, meta, timestamp: new Date().toISOString() });
  if (immediate) {
    flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => flush(), FLUSH_INTERVAL_MS);
  }
};

if (typeof window !== "undefined") {
  // Tab backgrounded or closed — flush whatever's queued via sendBeacon so
  // nothing queued in the last <2s window is silently lost.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

export const logger = {
  info: (message, meta) => enqueue("info", message, meta, false),
  warn: (message, meta) => enqueue("warn", message, meta, false),
  // Errors flush immediately — we don't want failure visibility delayed
  // behind the batching window.
  error: (message, meta) => enqueue("error", message, meta, true),
};
